import { PessoaDTO } from "@/types/pessoa";
import { pessoaService } from "@/services/api/PessoaService";
import { useEffect, useRef } from "react";

interface UseRehydrateUserParams {
  /** Só revalida quando há uma sessão local (token + usuário em cache). */
  enabled: boolean;
  /** Recebe o usuário fresco vindo do servidor. */
  onUser: (user: PessoaDTO) => void;
  onSettled?: () => void;
}

/**
 * Revalida o usuário logado contra o servidor (GET /me) uma vez, ao montar a
 * aplicação. É isto que faz um simples reload refletir mudanças feitas no
 * back-end — como passar a ficar ausente — sem precisar deslogar e logar de
 * novo. A revalidação roda em background: o snapshot do cache continua válido
 * até a resposta chegar, então não há flash de tela de login.
 */
export function useRehydrateUser({ enabled, onUser, onSettled }: UseRehydrateUserParams): void {
  const hasRun = useRef(false);

  useEffect(() => {
    if (!enabled || hasRun.current) return;
    hasRun.current = true;

    let active = true;
    pessoaService
      .getMe()
      .then((pessoa) => {
        if (active) onUser(pessoa);
      })
      .catch(() => {
        // 401 já é tratado pelo interceptor do httpClient (refresh/logout).
        // Erros de rede/5xx não devem derrubar a sessão: mantemos o cache.
      })
      .finally(() => {
        if (active) onSettled?.();
      });

    return () => {
      active = false;
    };
  }, [enabled, onUser, onSettled]);
}
